/**
 * IdentityShell — what an identity-only person sees when the auto-switch has
 * nowhere to send them (§8.2 and §8.3, Lane C C2/C3).
 *
 * Two shapes, one component, because they are the same screen with a different
 * middle — and they answer to DIFFERENT audiences:
 *
 *  - **The picker**, for anyone with two or more organizations, identity-only
 *    or not (decision 2026-08-14). The auto-switch deliberately refuses to
 *    guess: taking the FIRST of several is a silent choice between destinations
 *    only the person can rank, and it is not even stable, since it follows
 *    `my-tenants` order. A full-tenant person's own drive is one of the cards;
 *    an identity-only person's is absent, because it is not a destination.
 *  - **The org-less state**, identity-only ONLY. Somebody with an account, no
 *    drive of their own and no organization yet; the only route forward is an
 *    invitation, so invitations are CARDS on the page rather than something to
 *    go looking for. A full-tenant person with no organization has their drive
 *    and is missing nothing, so this screen would be a lie for them.
 *
 * ── Why an overlay and not a replacement tree ───────────────────────────────
 *
 * Settings has to work here (§8.3: "a Settings item in that menu opening a
 * personal-settings window"), and `SettingsView` takes ~40 props that App.tsx
 * already assembles for the settings deck. Rendering a second copy would mean
 * maintaining that prop wall twice — and Lane C may not edit `SettingsView` at
 * all. So the shell is an overlay over the mounted tree, the taskbar is hidden
 * beneath it, and opening Settings simply navigates: the existing deck comes up
 * full-width, and this overlay steps aside for it (`settingsOpen`), keeping only
 * the avatar so there is always a way back out.
 *
 * ── What is deliberately NOT here ───────────────────────────────────────────
 *
 * `AvatarMenu` is not mounted here at all, in either state. That component's job
 * is switching identity: on the picker the cards already do it, and on the
 * org-less screen there is nothing to switch to. What the menu used to carry
 * lives on the page instead — invitations as cards, "New organization" as an
 * action card (its own `canCreateOrg` gate, §8.7) — which leaves both states the
 * same footer of Settings / avatar / Log out.
 *
 * Invitations are deliberately absent from the PICKER: that person has
 * organizations to enter, and the taskbar's menu carries their invites once
 * inside.
 *
 * ── The trap this screen is one wrong boolean away from ─────────────────────
 *
 * `tenants.length === 0` is ALSO true while `my-tenants` is in flight, and
 * `identityOnly` is deliberately `false` until it lands. So the caller gates on
 * `tenantsSettled` — a RESOLVED emptiness — never on the raw list. Ungated, the
 * org-less shell paints over every user's screen on every cold load, which is
 * invisible on a dev machine and obvious on a throttled connection (§9.1).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Settings, LogOut, Building2, X, ShieldBan } from 'lucide-react'
import {
  AccountSheet, AccountActionCard,
  useTheme, getThemeClass, getInitials, avatarBg,
  type IdentityRow,
} from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'

interface TenantInfo {
  id: string
  name: string
  type: string
  emails: Array<{ email: string; role: string }>
}

interface PendingInvite {
  inviteId: string
  token: string
  tenantId: string
  tenantName: string
  email: string
  role: string
}

export interface IdentityShellProps {
  user: { name: string; email: string; image?: string }
  /**
   * The switchable candidates. Non-empty renders the picker; empty renders the
   * org-less state.
   *
   * Includes the ACTIVE tenant when it is one of them — a full-tenant person is
   * choosing between their own drive and their organizations, and the one they
   * are standing in wears the active card's shine border rather than being
   * dropped. An identity-only person's personal tenant is simply not in this
   * list, so C1's "no dead-end Personal entry" holds without a branch here.
   */
  pickerTenants: TenantInfo[]
  /** A destination was chosen. The caller uses this to stand the picker down
   *  for the rest of this page load, including when the pick is the tenant the
   *  person is already in (which changes no other state). */
  onPicked?: () => void
  activeTenantId: string
  activeOrgEmail?: string
  pendingInvites?: PendingInvite[]
  onSwitchTenant: (tenantId: string, orgEmail?: string) => void
  onCreateOrg: () => void
  /** `undefined` = unresolved, and still renders the control (§8.7 / §9.1). */
  canCreateOrg?: boolean
  onOpenSettings: () => void
  onSignOut: () => void
  onAcceptInvite?: (token: string) => void
  onDeclineInvite?: (token: string) => void
  onBlockInvite?: (token: string) => void
  /**
   * The settings deck is showing. The overlay yields to it — no fog, no cards,
   * click-through — and keeps only the avatar, which stays the way back.
   */
  settingsOpen?: boolean
  /**
   * A modal to render INSIDE this overlay's portal — currently the
   * org-create dialog.
   *
   * ⚠ It cannot simply be rendered by the caller, and both reasons are this
   * component's doing. The dialog mounts in `#root`, so (1) it paints at its own
   * `z-50` BENEATH this overlay's `z-150` fog, and (2) the `inert` sweep above
   * marks `#root` — disabling it completely. Together: invisible AND dead, which
   * is exactly how it was reported.
   *
   * Taking the ELEMENT rather than a flag keeps every piece of creation logic
   * (the tenant-list splice, the switch, the close) in the shell that owns it;
   * only the mount point moves.
   */
  overlay?: ReactNode
}

export function IdentityShell({
  user, pickerTenants, onPicked, activeTenantId, activeOrgEmail,
  pendingInvites, onSwitchTenant, onCreateOrg, canCreateOrg, onOpenSettings, onSignOut,
  onAcceptInvite, onDeclineInvite, onBlockInvite, settingsOpen, overlay,
}: IdentityShellProps) {
  const { t } = useTranslation('core-ui')
  const [confirmBlockToken, setConfirmBlockToken] = useState<string | null>(null)
  const invites = pendingInvites ?? []
  const hasInvites = invites.length > 0
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  /**
   * Take the app behind the takeover out of reach — the same guarantee
   * `@papan/core-ui`'s `Modal` gives, and for the same reason: covering the page
   * with a fog hides it from the eye only. Found in a DevTools a11y pass, which
   * reported one focusable element outside the shell (the drive's theme toggle)
   * while the picker was up — so a keyboard user could Tab onto a control they
   * could not see, on a screen whose whole point is that there is nothing else
   * to do yet.
   *
   * `inert` is also what makes a focus TRAP unnecessary: the browser skips inert
   * subtrees entirely, so Tab already cycles within the shell.
   *
   * Three things this depends on:
   *  - The shell is PORTALLED to `<body>`, so it is a direct body child and
   *    "every body child except mine" is a correct sweep. Rendered inline it
   *    would be a descendant of the app root, and marking that inert would take
   *    the shell with it — `Modal` carries the same warning.
   *  - It SKIPS `[data-overlay-root]` — the toast container, itself portaled to
   *    body. Toasts are feedback and must stay live above the takeover, and this
   *    screen raises its own (invite accept/decline/block). Without the skip,
   *    the sweep would inert them: visible at z-[10000] but unreadable and
   *    undismissable.
   *  - It is skipped while `settingsOpen`, because then the deck BEHIND is the
   *    intended target. Sweeping there would render an unusable Settings window
   *    with no way back.
   *
   * A toast raised AFTER this runs needs no special handling: it is a fresh body
   * child the sweep never saw, so it is never inerted. Only a toast already up
   * at mount is in the sweep's path, and the skip attribute covers exactly that.
   */
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (settingsOpen) return
    const root = rootRef.current
    if (!root) return
    const marked: HTMLElement[] = []
    for (const el of document.body.children) {
      const node = el as HTMLElement
      if (
        node === root || node.contains(root)
        || node.hasAttribute('inert')
        || node.hasAttribute('data-overlay-root')
      ) continue
      node.setAttribute('inert', '')
      marked.push(node)
    }
    return () => { for (const node of marked) node.removeAttribute('inert') }
  }, [settingsOpen])

  // Same flattening the phone launcher does — one row per tenant, except a
  // multi-email org gets one row per identity, since "which of my addresses"
  // is part of the choice.
  const rows: IdentityRow[] = pickerTenants.flatMap((tenant): IdentityRow[] => {
    // A personal tenant reaching this list is a FULL one (an identity-only
    // tenant never enters `pickerTenants`), so it is a real destination. It is
    // labelled by the person, not by the tenant's internal name, exactly as the
    // phone launcher's switcher does.
    if (tenant.type === 'personal') {
      return [{
        key: tenant.id,
        tenantId: tenant.id,
        kind: 'personal',
        label: user.name,
        sub: user.email,
        tag: 'Personal',
        seed: user.email,
        initials: getInitials(user.name),
        active: tenant.id === activeTenantId,
      }]
    }
    if (tenant.emails.length > 1) {
      return tenant.emails.map((e) => ({
        key: `${tenant.id}:${e.email}`,
        tenantId: tenant.id,
        kind: 'organization',
        email: e.email,
        label: tenant.name,
        sub: e.email,
        tag: e.role,
        seed: tenant.name,
        initials: getInitials(tenant.name),
        active: tenant.id === activeTenantId && (activeOrgEmail ?? user.email) === e.email,
      }))
    }
    return [{
      key: tenant.id,
      tenantId: tenant.id,
      kind: 'organization',
      email: tenant.emails[0]?.email,
      label: tenant.name,
      sub: tenant.emails[0]?.email,
      tag: tenant.emails[0]?.role,
      seed: tenant.name,
      initials: getInitials(tenant.name),
      active: tenant.id === activeTenantId,
    }]
  })

  /**
   * The picker's footer: the avatar, flanked by its two actions.
   *
   * NOT the taskbar's `AvatarMenu`. That component's job is switching identity,
   * and on this screen the cards already do that — so reusing it meant a
   * dropdown whose only remaining purpose was to hide two buttons behind a
   * click. Here the avatar is pure identity ("you are signed in as this
   * person"), rendered as a non-interactive element rather than a disabled
   * button, and Settings / Log out sit beside it where they can be read and hit
   * directly.
   *
   * The ORG-LESS screen keeps the full menu instead (see below): it has no
   * cards, so it genuinely needs the switcher's other contents — pending
   * invitations, which are that person's only route forward, and "New
   * organization".
   */
  const pickerFooter = (
    // z-20 over the sheet's z-10 — DOM order does not settle this. A positioned
    // element with `z-index: auto` paints BELOW a z-10 sibling regardless of
    // which comes later, so without this the footer sits under the picker's
    // scroll area: unreachable on the one screen it belongs to.
    <div className="pointer-events-auto absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
      <button
        type="button"
        onClick={onOpenSettings}
        title="Settings"
        aria-label="Settings"
        className="flex h-9 w-9 items-center justify-center rounded-full themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
      >
        <Settings size={17} />
      </button>

      {/* Identity, not a control: no click, no focus stop. The accessible name
          carries the person so a screen reader still announces who is signed
          in — `title` alone would not, on a non-interactive element. */}
      <div
        role="img"
        aria-label={`Signed in as ${user.name} (${user.email})`}
        title={`${user.name} · ${user.email}`}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white ${avatarBg(user.email)}`}
      >
        {getInitials(user.name)}
      </div>

      <button
        type="button"
        onClick={onSignOut}
        title="Log out"
        aria-label="Log out"
        className="flex h-9 w-9 items-center justify-center rounded-full text-red-400 hover:bg-red-500/15 transition-colors"
      >
        <LogOut size={17} />
      </button>
    </div>
  )

  const avatar = pickerFooter

  const body = settingsOpen ? null : rows.length > 0 ? (
    // `onClose` is a no-op on purpose: there is nothing behind this screen to
    // dismiss to. The sheet's fog-tap and Escape simply do nothing here — the
    // way past this screen is to choose, which is the only thing it asks.
    //
    // Picking the identity you are ALREADY on switches nothing, so it must not
    // navigate: `switchTenant` always ends in `navigate(landingPath(...))`, which
    // would throw away whatever path the person arrived on. It stands the picker
    // down instead.
    //
    // Every other pick relies on the NAVIGATION to stand the picker down (the
    // URL gains `/t/<id>`, which suppresses it) rather than on an optimistic
    // flag. That is deliberate: `switchTenant` returns silently when the
    // active-email POST is refused, and a flag set beforehand would retire the
    // picker while leaving the person exactly where they were, with no shell and
    // nothing to explain it. Doing nothing leaves the picker up, which is the
    // honest outcome — they can pick again.
    <AccountSheet
      rows={rows}
      onPick={(row) => {
        const sameIdentity = row.tenantId === activeTenantId
          && (!row.email || row.email === activeOrgEmail)
        if (sameIdentity) { onPicked?.(); return }
        onSwitchTenant(row.tenantId, row.email)
      }}
      onClose={() => {}}
    >
      {canCreateOrg !== false && (
        <AccountActionCard icon={<Plus size={16} />} label="New organization" onClick={onCreateOrg} />
      )}
    </AccountSheet>
  ) : (
    <div className="absolute inset-0 z-10">
      <div className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0" aria-hidden />
      <div className="absolute inset-0 flex items-center justify-center overflow-y-auto p-8">
        <div className="w-full max-w-md" data-testid="identity-shell-no-org">
          {/* The explainer answers "why is there nothing here?" — a question a
              pending invitation has already answered. So it renders only when
              there is no invitation to show; otherwise the card IS the message,
              and leading with prose about having no organization would argue
              with the organization sitting right below it. */}
          {!hasInvites && (
            <div className="text-center">
              <h2 className="themed-text text-base font-medium mb-2">
                {t('identityOnly.noOrgTitle', { defaultValue: 'No organization yet' })}
              </h2>
              <p className="themed-text-secondary text-sm leading-relaxed">
                {t('identityOnly.noOrgBody', {
                  defaultValue:
                    'This account is set up for working inside organizations rather than keeping files of its own. Once you accept an invitation, that organization opens here.',
                })}
              </p>
            </div>
          )}

          {/* Invitations are ON THE PAGE, not folded into a menu.
              This screen's whole subject is "you have no organization yet", and
              an invitation is the answer to it — so burying it behind an avatar
              click was asking the person to go looking for the one thing they
              came for. The previous copy literally said "invitations are in the
              account menu below", which is a sentence a screen should not need.
              There is a whole empty middle here; the invites belong in it. */}
          {hasInvites && (
            <div className="flex flex-col gap-2">
              <p className="px-1 text-[10px] uppercase tracking-wide themed-text-muted">
                {invites.length === 1 ? 'Invitation' : 'Invitations'}
              </p>
              {invites.map((inv) => (
                <div
                  key={inv.inviteId}
                  className="mobile-account-card flex shrink-0 items-center gap-2.5 border themed-border themed-bg-secondary px-3.5 py-2.5 text-left"
                >
                  <span className="ws-project-glyph shrink-0"><Building2 size={15} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm themed-text">{inv.tenantName}</span>
                    <span className="block truncate text-xs themed-text-muted">{inv.email} · {inv.role}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => onAcceptInvite?.(inv.token)}
                    className="shrink-0 rounded bg-green-600 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-green-500"
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeclineInvite?.(inv.token)}
                    title="Decline"
                    aria-label={`Decline the invitation to ${inv.tenantName}`}
                    className="shrink-0 rounded p-1 themed-text-muted transition-colors hover:themed-text hover:themed-bg-tertiary"
                  >
                    <X size={15} strokeWidth={2.5} />
                  </button>
                  {/* Confirm-first, exactly as the taskbar menu does it: block is
                      irreversible from this screen and sits next to Decline. */}
                  {confirmBlockToken === inv.token ? (
                    <button
                      type="button"
                      onClick={() => { onBlockInvite?.(inv.token); setConfirmBlockToken(null) }}
                      className="shrink-0 rounded px-2 py-1 text-[10px] font-semibold text-red-400 transition-colors hover:bg-red-500/20"
                    >
                      Confirm block
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmBlockToken(inv.token)}
                      title="Block"
                      aria-label={`Block invitations from ${inv.tenantName}`}
                      className="shrink-0 rounded p-1 themed-text-muted transition-colors hover:text-red-400 hover:themed-bg-tertiary"
                    >
                      <ShieldBan size={15} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* An identity-only person CAN create an organization — the mode gates
              writes to their own drive, while this provisions a new tenant. So
              this is a real route forward, not a button that 403s; verified
              against the live route. It hides only on a RESOLVED refusal (§8.7),
              which is what a deployment with `max_free_orgs_per_user = 0` sends.

              CENTRED: the card is content-width (`.mobile-account-card` sets
              `flex: 0 0 auto` and no width), so left-aligning it under a
              full-width invitation card left it visibly off-axis from the
              heading and copy above. */}
          {canCreateOrg !== false && (
            <div className="mt-6 flex justify-center">
              <AccountActionCard icon={<Plus size={16} />} label="New organization" onClick={onCreateOrg} />
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // ws-clear is LOAD-BEARING, same rule as the launcher and the sheet: a theme
  // class paints an opaque --bg-primary, which would sit between the fog and the
  // app, so the blur would sample the wrapper's own paint and frost nothing.
  return createPortal(
    <div
      ref={rootRef}
      className={`${themeClass} ws-clear fixed inset-0 z-[150] ${settingsOpen ? 'pointer-events-none' : ''}`}
      data-testid="identity-shell"
    >
      {body}
      {avatar}
      {/* Above the fog and outside the inert sweep — see `overlay`. */}
      {overlay && <div className="pointer-events-auto absolute inset-0 z-30">{overlay}</div>}
    </div>,
    document.body,
  )
}
